import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    ViewChild, ViewEncapsulation,
    inject,
    ChangeDetectionStrategy
} from '@angular/core';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Prism from 'prismjs';

import { NgClass } from '@angular/common';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-scss';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-xml-doc';

@Component( {
    selector: 'his-display-code',
    templateUrl: './his-display-code.component.html',
    styleUrls: ['./his-display-code.component.scss'],
    imports: [NgClass],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
} )
export class HisDisplayCodeComponent implements OnChanges, AfterViewInit {
    @Input() code = '';
    @Input() language = 'typescript';
    @Input() showLineNumbers = false;
    /**
     * Highlights the specified lines in the code block.\
     * Accepts a string of comma-separated line numbers or an array of line numbers to highlight.\
     * Also supports ranges of lines using the `-` character, e.g., "2-4".
     * ```
     * <his-display-code language="html" [showLineNumbers]="true" [code]="code" highlightLines="1,10,15,26-28" />
     * ```
     */
    @Input() highlightLines: string | number[] = '';

    @ViewChild( 'codeElement', { static: true } ) codeElement!: ElementRef;
    @ViewChild( 'preElement', { static: true } ) preElement!: ElementRef;

    highlightedCode = '';
    lineNumbers: string[] = [];

    protected highlightedLineSet = new Set<number>();
    protected highlightedLineList: number[] = [];

    private el = inject( ElementRef );
    private cdr = inject( ChangeDetectorRef );

    ngAfterViewInit(): void {
        if ( !this.code ) {
            const container = this.el.nativeElement.querySelector( '.legacy-content' );
            const childCode = container?.querySelector( 'code' );

            console.log( '[highlight] innerHTML:', container.innerHTML );
            console.log( '[highlight] textContent:', container.textContent );
            if ( childCode ) {
                this.code = childCode.textContent ?? '';
                const langClass = Array.from( childCode.classList as DOMTokenList )
                    .find( ( c: string ) => c.startsWith( 'language-' ) );
                if ( langClass ) {
                    this.language = langClass.replace( 'language-', '' );
                }
            } else if ( container?.innerHTML?.trim() ) {
                this.code = this.decodeHtmlEntities( container.innerHTML.trim() );
            }

            if ( this.code ) {
                this.highlightCode();
                this.cdr.detectChanges();
            }
        }
    }

    ngOnChanges(): void {
        this.highlightCode();
        this.parseHighlightLines();
    }

    private decodeHtmlEntities( html: string ): string {
        const div = document.createElement( 'div' );
        div.innerHTML = html;
        return div.textContent ?? '';
    }

    private highlightCode() {
        if ( this.code ) {
            try {
                const grammar = Prism.languages[this.language];
                if ( grammar ) {
                    this.highlightedCode = Prism.highlight( this.code, grammar, this.language );
                } else {
                    this.highlightedCode = this.escapeHtml( this.code );
                }

                if ( this.showLineNumbers ) {
                    this.generateLineNumbers();
                }
            } catch ( error ) {
                console.warn( 'Syntax highlighting failed:', error );
                this.highlightedCode = this.escapeHtml( this.code );
            }
        }
    }

    private generateLineNumbers() {
        const lines = this.code.split( '\n' );
        this.lineNumbers = lines.map( ( _, index ) => ( index + 1 ).toString() );
    }

    private escapeHtml( text: string ): string {
        const div = document.createElement( 'div' );
        div.textContent = text;
        return div.innerHTML;
    }

    // Accepts "1,3-5" or [1, 3, 4, 5]
    private parseHighlightLines() {
        const set = new Set<number>();
        if ( Array.isArray( this.highlightLines ) ) {
            this.highlightLines.forEach( n => set.add( n ) );
        } else {
            for ( const part of this.highlightLines.split( ',' ) ) {
                const [from, to = from] = part.trim().split( '-' ).map( Number );
                if ( Number.isInteger( from ) && Number.isInteger( to ) ) {
                    for ( let i = from; i <= to; i++ ) {
                        set.add( i );
                    }
                }
            }
        }
        this.highlightedLineSet = set;
        this.highlightedLineList = Array.from( set ).sort( ( a, b ) => a - b );
    }
}